import { useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  loadState,
  useCheckTutorialCompleted,
} from 'components/Common/Tutorial/_methods'
import { setCurrentStep } from 'store/tutorial'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { getPersonCategory } from 'utils/company'
import { UIMatch, useMatches } from 'react-router-dom'
import { useCheckMobile } from 'utils/hooks/useMediaQuery'
import type { RouteHandle } from 'components/Common/Menu'
import { getUserRole } from 'store/me/selectors'

interface IntersectionObserverInit {
  root?: Element | Document | null
  rootMargin?: string
  threshold?: number | number[]
}

export const useElementVisible = (
  element: Element | null,
  options?: IntersectionObserverInit
) => {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (!element) {
      return
    }

    const observer = new IntersectionObserver(([entry]) => {
      setIsVisible(entry.isIntersecting)
    }, options)

    observer.observe(element)

    return () => observer.disconnect()
  }, [element, JSON.stringify(options)])

  return isVisible
}

export const useGetTutorialSteps = () => {
  const { t } = useTranslation()
  const matches = useMatches() as UIMatch<unknown, RouteHandle>[]
  const isMobile = useCheckMobile()
  const category = getPersonCategory()
  const role = useAppSelector(getUserRole)

  return useMemo(() => {
    const tutorialSteps = matches
      .find(match => match.handle && match.handle?.getTutorialData)
      ?.handle?.getTutorialData?.({
        t,
        isMobile,
        category,
        role,
      })
    return tutorialSteps ?? []
  }, [t, matches, isMobile, category])
}

export const useTutorialAutoStart = (pageName: string) => {
  const dispatch = useAppDispatch()

  useCheckTutorialCompleted(pageName, completed => {
    const isFirstPageVisit = loadState(pageName) === undefined
    const shouldAutoStartTutorial = !completed && isFirstPageVisit
    if (shouldAutoStartTutorial) {
      dispatch(setCurrentStep(0))
    }
  })
}
